// Per-SKU factory reorder plan for the SKU Family Live tool: when each SKU
// has to be ordered (given factory lead time) and how much, so reorders aren't
// sized off a static daily run rate. Pure functions, no React.
//
// Per SKU:
//   demand/day  = D2C + B2B, each forecast separately (skuForecast.js):
//                 its last-12-months daily rate in that channel brought
//                 forward to today, then grown by that channel's
//                 year-over-year rate for 12 months, flat after
//   safety      = z * monthly std dev of units (in-stock months, last 12) *
//                 sqrt(lead time in months) — lumpy SKUs (big wholesale POs)
//                 carry more buffer than steady ones
//                 (capped at MAX_SAFETY_DAYS of demand)
//   order-by    = first day from lead time on (the earliest a new order can
//                 land) that projected stock, incl. the restock plan, drops
//                 below safety, minus lead time. A stock-out before then
//                 can't be fixed by a new order and is flagged separately.
//   reorder qty = demand over the coverage period after arrival + safety
//                 - stock projected on arrival, rounded up to master cartons
import { parseDate, daysBetween } from './skuFamilyLiveEngine';
import { skuForecast, demandOnDay } from './skuForecast';

export const REORDER_DEFAULTS = { leadTimeDays: 120, coverageDays: 180, serviceZ: 1.65 };

const DAYS_PER_MONTH = 30.44;
const HORIZON_DAYS = 730;          // look this far ahead for the stock-out
const ORDER_SOON_DAYS = 30;
const MAX_SAFETY_DAYS = 90;        // one launch PO can make sigma huge

// Std dev of monthly units over the SKU's in-stock months (null if < 3).
function monthlyStdDev(skuEntry) {
  const w = skuEntry?.window_12mo;
  if (!w) return null;
  const skipped = new Set(w.excluded_months || []);
  const y = w.actual_units.filter((_, i) => !skipped.has(w.months[i]));
  if (y.length < 3) return null;
  const mean = y.reduce((a, b) => a + b, 0) / y.length;
  return Math.sqrt(y.reduce((a, v) => a + (v - mean) ** 2, 0) / (y.length - 1));
}

export function reorderRow(s, { skuEntry, familyEntry, restocks, asOf, leadTimeDays, coverageDays, serviceZ }) {
  const fc = skuForecast(s, skuEntry, familyEntry);
  const rate = fc.today;
  const demandOn = i => demandOnDay(fc, i);

  const sigma = monthlyStdDev(skuEntry);
  let safety = sigma != null
    ? serviceZ * sigma * Math.sqrt(leadTimeDays / DAYS_PER_MONTH)
    : 30 * rate;                                      // no history: 30 days of demand
  let safetyBasis = sigma != null ? 'volatility' : '30 days (no history)';
  if (safety > MAX_SAFETY_DAYS * rate) {
    safety = MAX_SAFETY_DAYS * rate;
    safetyBasis = `capped at ${MAX_SAFETY_DAYS} days`;
  }

  const incomingByDay = new Map();
  let incomingTotal = 0, nextIncoming = null;
  for (const ev of restocks) {
    const d = daysBetween(asOf, parseDate(ev.date));
    if (d < 0) continue;
    incomingByDay.set(d, (incomingByDay.get(d) || 0) + ev.qty);
    incomingTotal += ev.qty;
    if (nextIncoming == null || ev.date < nextIncoming) nextIncoming = ev.date;
  }

  // Projected stock without any new order; lost sales aren't backordered.
  const proj = new Array(HORIZON_DAYS + coverageDays + leadTimeDays + 1);
  let stock = s.stock, breachDay = null, stockoutDay = null;
  for (let i = 0; i < proj.length; i++) {
    stock += incomingByDay.get(i) || 0;
    if (i > 0) stock = Math.max(0, stock - demandOn(i));
    proj[i] = stock;
    if (breachDay == null && i >= leadTimeDays && i <= HORIZON_DAYS + leadTimeDays && stock < safety) breachDay = i;
    if (stockoutDay == null && i <= HORIZON_DAYS && stock <= 0) stockoutDay = i;
  }

  let status, orderByDay = null, qty = 0;
  if (rate <= 0) {
    status = 'No sales';
  } else if (breachDay == null) {
    status = 'OK';
  } else {
    orderByDay = breachDay - leadTimeDays;
    status = orderByDay === 0 ? 'Order now' : orderByDay <= ORDER_SOON_DAYS ? 'Order soon' : 'OK';
    const arrival = orderByDay + leadTimeDays;
    let need = safety - proj[arrival];
    for (let i = arrival + 1; i <= arrival + coverageDays; i++) {
      need += demandOn(i) - (incomingByDay.get(i) || 0);
    }
    qty = Math.max(0, Math.ceil(need));
    if (qty === 0) status = 'OK';
  }
  const cartons = s.master_qty && qty > 0 ? Math.ceil(qty / s.master_qty) : null;
  if (cartons != null) qty = cartons * s.master_qty;

  return {
    sku: s.sku, family: s.family, color: s.color, title: s.title,
    stock: s.stock, incomingTotal, nextIncoming,
    rawRate: s.rate || 0, rate, rateBasis: fc.basis, fc,
    yoy: skuEntry?.yoy || null,
    monthsOfSupply: rate > 0 ? (s.stock + incomingTotal) / (rate * DAYS_PER_MONTH) : null,
    safety: Math.ceil(safety), safetyBasis,
    stockoutDay, orderByDay, status,
    // Runs out before a new order could land, even if ordered today.
    gapBeforeArrival: stockoutDay != null && stockoutDay < leadTimeDays,
    qty, cartons, masterQty: s.master_qty,
  };
}

const STATUS_ORDER = { 'Order now': 0, 'Order soon': 1, OK: 2, 'No sales': 3 };

export function buildReorderPlan(skus, { skuGrowthData, growthData, restockEvents, asOf, ...settings }) {
  const bySku = new Map();
  for (const ev of restockEvents || []) {
    if (!bySku.has(ev.sku)) bySku.set(ev.sku, []);
    bySku.get(ev.sku).push(ev);
  }
  return skus
    .map(s => reorderRow(s, {
      skuEntry: skuGrowthData ? skuGrowthData[s.sku] : null,
      familyEntry: growthData ? growthData[s.family] : null,
      restocks: bySku.get(s.sku) || [],
      asOf, ...settings,
    }))
    .sort((a, b) => (STATUS_ORDER[a.status] - STATUS_ORDER[b.status])
      || ((a.orderByDay ?? Infinity) - (b.orderByDay ?? Infinity))
      || a.sku.localeCompare(b.sku));
}
